import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/InvitationsPage.css'; // Reusing modal styles

const ContractSelectionModal = ({ 
  onClose, 
  onSelectContract, 
  contracts: propContracts = null, // Optional: contracts passed as props
  title = "Select a Contract to Explore",
  description = "Choose a contract to view the client experience for that contract. You can search by contract name or contract number to find specific contracts."
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContractId, setSelectedContractId] = useState(null);

  // Fetch contracts from the API
  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      
      let response;
      if (user?.role === 'client') {
        // For client users, fetch only their assigned contracts
        response = await fetch(`${apiBaseUrl}/api/users/${user.id}/contracts`);
      } else {
        // For admin/team users, fetch all contracts
        response = await fetch(`${apiBaseUrl}/api/contracts`);
      }
      
      if (!response.ok) {
        throw new Error(`Failed to fetch contracts: ${response.status}`);
      }
      
      const data = await response.json();
      setContracts(data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setError('Failed to load contracts. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [user]);
  
  // Load contracts on mount (only if not provided as props)
  useEffect(() => {
    if (propContracts) {
      // Use contracts passed as props
      setContracts(propContracts);
      setLoading(false);
    } else {
      // Fetch contracts from API
      fetchContracts();
    }
  }, [fetchContracts, propContracts]);

  // Filter contracts based on search term
  const filteredContracts = contracts.filter(contract => {
    if (!searchTerm) return true; // Show all contracts if no search term
    
    const searchLower = searchTerm.toLowerCase();
    // Handle both camelCase and snake_case field names
    const contractName = contract.contractName || contract.contract_name || '';
    const contractNumber = contract.contractNumber || contract.external_id || '';
    
    return contractName.toLowerCase().includes(searchLower) || 
           contractNumber.toLowerCase().includes(searchLower);
  }).sort((a, b) => {
    // Sort alphabetically by contract name
    const nameA = (a.contractName || a.contract_name || '').toLowerCase();
    const nameB = (b.contractName || b.contract_name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle contract selection
  const handleContractClick = (contractId) => {
    setSelectedContractId(contractId);
  };

  // Handle view as client button click
  const handleViewAsClient = () => {
    if (selectedContractId) {
      onSelectContract(selectedContractId);
    }
  };

  // Add some debugging
  console.log('ContractSelectionModal - User role:', user?.role);
  console.log('ContractSelectionModal - Contracts loaded:', contracts.length);
  console.log('ContractSelectionModal - Search term:', searchTerm);
  console.log('ContractSelectionModal - Filtered contracts:', filteredContracts.length);
  if (contracts.length > 0) {
    console.log('ContractSelectionModal - Sample contract:', contracts[0]);
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ width: '80%', maxWidth: '800px', maxHeight: '80vh' }}>
        <h2 className="modal-header">{title}</h2>
        <p>{description}</p>
        
        {/* Search input */}
        <div className="form-group">
          <input
            type="text"
            placeholder="Search by contract name or contract number..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="form-control"
            style={{ marginBottom: '20px' }}
          />
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="error-message" style={{ color: 'red', marginBottom: '20px' }}>
            {error}
          </div>
        )}

        {/* Loading Indicator */}
        {loading ? (
          <div className="loading-indicator" style={{ textAlign: 'center', padding: '40px 0' }}>
            Loading contracts...
          </div>
        ) : (
          /* Contracts List */
          <div style={{ overflowY: 'auto', maxHeight: '400px' }}>
            {contracts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                {user?.role === 'client' 
                  ? 'No contracts assigned to your account. Please contact your administrator.'
                  : 'No contracts loaded. Please check your connection and try again.'
                }
              </div>
            ) : filteredContracts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                No contracts found matching "{searchTerm}". Try a different search term.
              </div>
            ) : (
              <table className="invitations-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>Contract Name</th>
                    <th>Contract Number</th>
                    <th>Business Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContracts.map((contract) => {
                    // Handle both API response formats
                    const contractId = contract.contractId || contract.contract_id;
                    const contractName = contract.contractName || contract.contract_name || 'N/A';
                    const contractNumber = contract.contractNumber || contract.external_id || 'N/A';
                    const businessUnit = contract.businessUnit || contract.business_unit_name || 'N/A';
                    
                    return (
                      <tr 
                        key={contractId} 
                        onClick={() => handleContractClick(contractId)}
                        className={selectedContractId === contractId ? 'selected-row' : ''}
                        style={{ 
                          cursor: 'pointer',
                          background: selectedContractId === contractId ? '#f0e6ff' : 'inherit'
                        }}
                      >
                        <td>
                          <input 
                            type="radio" 
                            name="contract" 
                            checked={selectedContractId === contractId}
                            onChange={() => handleContractClick(contractId)}
                          />
                        </td>
                        <td>{contractName}</td>
                        <td>{contractNumber}</td>
                        <td>{businessUnit}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
        
        {/* Modal Actions */}
        <div className="form-actions" style={{ marginTop: '20px' }}>
          <button 
            type="button" 
            className="action-button" 
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="action-button primary-button"
            disabled={!selectedContractId || loading}
            onClick={handleViewAsClient}
          >
            {user?.role === 'client' ? 'View Contract' : 'View as Client'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractSelectionModal; 